import { keyBy, uniq } from 'es-toolkit';

import { Organization, User } from '@paragon/contract-library';
import { notNullish } from '@paragon/utilities';

import { pFetchAll as pFetchAllJobs, pFetchAllRegions } from '../data/jobData';
import { pGetOrganization } from '../data/organizationData';
import { pFetchUserByEmail, pFetchUsersInOrganization } from '../data/userData';
import { pFetchAllForUser as pFetchAllViewsForUser } from '../data/viewData';
import { setJobs } from '../store/jobsSlice';
import { setUserOrganization, setUsersInOrganization, setVisibleOrganizations } from '../store/organizationsSlice';
import { setRegions } from '../store/regionsSlice';
import { setIsLoadingJobs } from '../store/statusesSlice';
import { setViews } from '../store/viewsSlice';
import { AsyncThunkActionReturn, AsyncThunkActionVoid } from './thunkActionTypes';

const pLoadCurrentOrganization = (): AsyncThunkActionReturn<Organization> => async (dispatch, getState) => {
  const organizationName = getState().logInData.loggedInUser!.organization;

  const organization = await pGetOrganization(organizationName);
  dispatch(setUserOrganization(organization));

  return organization;
};

export const loadOrganization = (): AsyncThunkActionVoid => async (dispatch) => {
  const organization = await dispatch(pLoadCurrentOrganization());
  dispatch(setVisibleOrganizations(organization.visibleOrganizations));

  const usersInOrganization = await pFetchUsersInOrganization(organization.name);
  dispatch(setUsersInOrganization(usersInOrganization));
};

export const loadRegions = (): AsyncThunkActionVoid => async (dispatch) => {
  const regions = await pFetchAllRegions();
  dispatch(setRegions(regions));
};

const pLoadJobs = (): AsyncThunkActionVoid => async (dispatch) => {
  dispatch(setIsLoadingJobs(true));

  try {
    const jobs = await pFetchAllJobs();
    dispatch(setJobs(jobs));
  } finally {
    dispatch(setIsLoadingJobs(false));
  }
};

const loadUsers = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const creatorEmails = getState().jobs.jobs.map((job) => job.creatorEmail);
  const assignedToUserEmails = getState().jobs.jobs.flatMap((job) => job.assignedToEmails);
  const openedByEmails = getState().jobs.jobs.flatMap((job) => job.openedByEmails);
  const userEmailsToLoad = uniq([...creatorEmails, ...assignedToUserEmails, ...openedByEmails]).filter(
    (uniqueEmail) => uniqueEmail != null && uniqueEmail !== '',
  );
  const users = await Promise.all(userEmailsToLoad.map(pFetchUserByEmail));
  // Once all users are loaded update jobs with user objects
  const usersByEmail: { [email: string]: User | undefined } = keyBy(users.filter(notNullish), (user) => user.email);
  const jobsWithUsers = getState().jobs.jobs.map((job) => {
    // Add user objects to jobs
    const creator = usersByEmail[job.creatorEmail];
    const location = creator && creator.location.length > 0 ? creator.location : 'No Location';

    const assignedTo = job.assignedToEmails
      .map((email) => usersByEmail[email])
      .filter(notNullish)
      .filter((assignedToUser) =>
        getState().organizations.usersInOrganization!.find((user) => user.email === assignedToUser.email),
      );

    const openedBy = job.openedByEmails.map((openedByEmail) => usersByEmail[openedByEmail]).filter(notNullish);

    return {
      ...job,
      creator,
      location,
      assignedTo,
      openedBy,
    };
  });

  dispatch(setJobs(jobsWithUsers));
};

export const loadJobs = (): AsyncThunkActionVoid => async (dispatch) => {
  await dispatch(pLoadJobs());
  await dispatch(loadUsers());
};

export const loadViews = (): AsyncThunkActionVoid => async (dispatch) => {
  const views = await pFetchAllViewsForUser();
  dispatch(setViews(views));
};
